
import { ArrowBack, Article, ChevronRight, Folder, PictureAsPdf } from '@mui/icons-material';
import { Button, Dialog, DialogContent, DialogTitle, Input, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { apiGet, AppState, DriveFile, driveFileSelector, driveFileSlice, DriveFolderMimeType, DrivePDFMimeType, DriveXLSXMimeType, PROJECTS_DRIVE_ID, searchDriveFolderContent } from 'cdx-common';
import React, { ComponentType, CSSProperties } from 'react';

import { useSelector } from 'react-redux';
import { FixedSizeList, FixedSizeListProps } from 'react-window';
import { appStore } from '../../store';
import { LoadingBackdrop } from '../utils/LoadingBackdrop';
// @ts-ignore comment
const List = FixedSizeList as ComponentType<FixedSizeListProps>;

export enum GoogleFinderMode {
    SELECT_FOLDER = 'SELECT_FOLDER',
    SELECT_PDF = 'SELECT_PDF',
    SELECT_XLSX = 'SELECT_XLSX',
}


export const GoogleFileFolderSelector = (props: { driveIdRoot?: string, mode: GoogleFinderMode, onFileSelected: (file?: DriveFile) => void; }) => {

    const [driveIdPath, setDriveIdPath] = React.useState<string[]>([]);
    const [isLoading, setLoading] = React.useState(false);

    const [search, setSearch] = React.useState('');

    const getMimeTypeFilter = React.useMemo(() => {
        switch (props.mode) {
            case GoogleFinderMode.SELECT_FOLDER:
                return DriveFolderMimeType;
            case GoogleFinderMode.SELECT_PDF:
                return [DriveFolderMimeType, DrivePDFMimeType];
            case GoogleFinderMode.SELECT_XLSX:
                return [DriveFolderMimeType, DriveXLSXMimeType];
            default:
                return undefined;
        }
    }, [props.mode]);

    const currentDriveFolderId = React.useMemo(() => {
        return driveIdPath.length ? driveIdPath[driveIdPath.length - 1] : undefined;
    }, [driveIdPath]);

    const currentDriveFolder = useSelector((state: AppState) => driveFileSelector.selectById(state, currentDriveFolderId ?? ''));
    const currentDriveFolderContent = useSelector((state: AppState) => searchDriveFolderContent(state, search, currentDriveFolderId ?? '', getMimeTypeFilter));

    const load = async (driveId: string) => {

        setLoading(true);
        const response = await apiGet('listGoogleDriveFolders', undefined, {
            driveId: driveId
        });
        appStore.dispatch(driveFileSlice.actions.upsertMany(response));
        setSearch('');
        setLoading(false);

    };

    React.useEffect(() => {
        if (currentDriveFolderId) {
            if (!currentDriveFolder || !currentDriveFolder.contentLoaded) {
                void load(currentDriveFolderId);
            }
        }
    }, [currentDriveFolderId, currentDriveFolder]);

    React.useEffect(() => {
        setDriveIdPath([props.driveIdRoot ?? PROJECTS_DRIVE_ID]);
    }, []);

    const onClose = () => {
        props.onFileSelected();
    };


    const loadFolderContent = (folderId: string) => {
        setDriveIdPath(p => p.concat([folderId]));
    };

    const onFileClick = (driveFile: DriveFile) => {
        setSearch('');
        if (driveFile.mimeType == DriveFolderMimeType) {
            loadFolderContent(driveFile.id);
        } else {
            props.onFileSelected(driveFile);
        }
    };

    const onSelectFolder = (driveFile: DriveFile) => {
        setSearch('');
        props.onFileSelected(driveFile);
    };

    const selectButton = (driveFile: DriveFile) => {
        return <Button size='small' variant='contained' onClick={() => onSelectFolder(driveFile)} title="Sélectionner ce dossier">Sélec.</Button>;
    };


    const Row = ({ index, style }: { index: number; style: CSSProperties; }) => (
        <ListItem
            style={style}
            secondaryAction={props.mode === GoogleFinderMode.SELECT_FOLDER && currentDriveFolderContent[index].mimeType === DriveFolderMimeType && selectButton(currentDriveFolderContent[index])}
        >
            <ListItemButton onClick={() => { onFileClick(currentDriveFolderContent[index]); }}>
                <ListItemIcon>
                    {currentDriveFolderContent[index].mimeType == 'application/vnd.google-apps.folder' ? <Folder /> :
                        (currentDriveFolderContent[index].mimeType == 'application/pdf' ? <PictureAsPdf /> : <Article />)
                    }
                </ListItemIcon>
                <ListItemText primary={<Typography noWrap title={currentDriveFolderContent[index].name}>{currentDriveFolderContent[index].name}</Typography>} />
            </ListItemButton>
        </ListItem>
    );

    const getTitle = React.useMemo(() => {
        switch (props.mode) {
            case GoogleFinderMode.SELECT_FOLDER:
                return 'Sélectionner le dossier';
            case GoogleFinderMode.SELECT_PDF:
                return 'Sélectionner le fichier pdf';
            case GoogleFinderMode.SELECT_XLSX:
                return 'Sélectionner le fichier excel';
            default:
                return 'Sélectionner le fichier';
        }
    }, [props.mode]);

    const onBackClick = () => {
        setSearch('');
        const p = [...driveIdPath];
        p.splice(p.length - 1);
        setDriveIdPath(p);
        //() => { load(driveIdPath[driveIdPath.length - 2]); }
    };

    return <Dialog open={true} onClose={onClose}>
        <DialogTitle>{getTitle}</DialogTitle>
        <DialogContent>
            <Box sx={{ width: 550, height: '100%', bgcolor: 'background.paper', position: 'relative' }}>

                {isLoading && <LoadingBackdrop sx={{ position: 'absolute' }} caption='Chargement' />}
                <Box sx={{ display: 'flex' }} p={1}>
                    <Button size='small' disabled={driveIdPath[0] == props.driveIdRoot || driveIdPath.length < 2} onClick={onBackClick}><ArrowBack /></Button>
                    <Input autoFocus sx={{ flexGrow: 1 }} name='filterName' id='filterName' placeholder='Rechercher...' value={search} onChange={event => {
                        setSearch(event.target.value);
                    }} />
                </Box>
                <Box p={1}>
                    <Typography variant='body2'><ChevronRight /> {currentDriveFolder?.name ?? ''}{props.mode === GoogleFinderMode.SELECT_FOLDER && currentDriveFolder && selectButton(currentDriveFolder)}</Typography>
                </Box>
                <List itemSize={35} height={400} width={500} itemCount={currentDriveFolderContent.length} style={{ height: 400, overflowY: 'scroll' }} >
                    {Row}
                </List >
            </Box>
        </DialogContent>
    </Dialog>;
};
